import { useEffect, useState, useRef } from 'react';
import { useMovieStore, useUIStore } from '../../stores';
import './MovieSelector.css';
import {
  PlusIcon,
  Cross1Icon,
  ChevronRightIcon,
  TrashIcon,
  ExitIcon,
  DotsHorizontalIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons';
import { FilmSlate } from '@phosphor-icons/react';
import { Button, DropdownMenu } from '@radix-ui/themes';
import * as Collapsible from '@radix-ui/react-collapsible';
import { usePost, usePatch, useOnClickOutside, useDelete } from '../../hooks';
import { useTranslation } from '../../i18n';
import type { MovieResponse, MovieSummary } from '../../types';
import { ConfirmModal } from '../ui/ConfirmModal';

const normalizeDimension = (value: number): number => (value > 0 ? value : 500);

const getMovieSummaryId = (movie: MovieSummary): number => movie.movieId ?? movie.id;

const mergeMovieSummaries = (
  ...candidates: Array<MovieSummary | null | undefined>
): MovieSummary | null => {
  const items = candidates.filter((item): item is MovieSummary => !!item);
  if (items.length === 0) return null;

  const merged: MovieSummary = { ...items[0] };
  for (const item of items) {
    if (item.name?.trim()) merged.name = item.name;
    if (item.description?.trim()) merged.description = item.description;
    if (item.width && item.width > 0) merged.width = item.width;
    if (item.height && item.height > 0) merged.height = item.height;
    if (item.fps && item.fps > 0) merged.fps = item.fps;
    if (item.sceneCount != null) merged.sceneCount = item.sceneCount;
    if (item.sceneObjectCount != null) merged.sceneObjectCount = item.sceneObjectCount;
    if (item.layerCount != null) merged.layerCount = item.layerCount;
    if (item.movieId != null) merged.movieId = item.movieId;
    if (item.isSample) merged.isSample = true;
    if (item.lastOpenedAt) merged.lastOpenedAt = item.lastOpenedAt;
  }
  return merged;
};

const isSamePreviewSummary = (a: MovieSummary, b: MovieSummary): boolean =>
  a.id === b.id &&
  (a.movieId ?? a.id) === (b.movieId ?? b.id) &&
  (a.name ?? '') === (b.name ?? '') &&
  (a.description ?? '') === (b.description ?? '') &&
  (a.width ?? 0) === (b.width ?? 0) &&
  (a.height ?? 0) === (b.height ?? 0) &&
  (a.fps ?? 0) === (b.fps ?? 0) &&
  (a.sceneCount ?? 0) === (b.sceneCount ?? 0) &&
  (a.sceneObjectCount ?? 0) === (b.sceneObjectCount ?? 0) &&
  (a.layerCount ?? 0) === (b.layerCount ?? 0) &&
  !!a.isSample === !!b.isSample;

type CreateMovieRequest = {
  name: string;
  width?: number;
  height?: number;
  fps?: number;
};

type UpdateMovieRequest = {
  name: string;
  description: string;
  width: number;
  height: number;
  fps: number;
};

type MovieSelectorProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function MovieSelector({ isOpen, onClose }: MovieSelectorProps) {
  const { t } = useTranslation();
  const [newMovieName, setNewMovieName] = useState('');
  const [newMovieDescription, setNewMovieDescription] = useState('');
  const [editedMovieName, setEditedMovieName] = useState('');
  const [editedMovieDescription, setEditedMovieDescription] = useState('');
  const [showNewMovieForm, setShowNewMovieForm] = useState(false);
  const [previewMovie, setPreviewMovie] = useState<MovieSummary | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { setWelcomeOpen } = useUIStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    currentMovie,
    updateMovie,
    deleteMovie,
    unloadMovie,
    userAllMovies,
    setUserAllMovies,
    userRecentMovies,
    setUserRecentMovies,
    setSelectedMovieId,
  } = useMovieStore();

  const previewMovieId = previewMovie ? getMovieSummaryId(previewMovie) : null;
  const currentMovieId = currentMovie?.id ? Number(currentMovie.id) : null;
  const isPreviewCurrentMovie =
    previewMovieId != null &&
    currentMovieId != null &&
    !Number.isNaN(currentMovieId) &&
    previewMovieId === currentMovieId;

  const { execute: createMovie } = usePost<MovieResponse, CreateMovieRequest>('/api/movies/create');
  const updateMovieUrl = previewMovieId ? `/api/movies/${previewMovieId}/update` : null;
  const deleteMovieUrl = previewMovieId ? `/api/movies/${previewMovieId}/delete` : null;
  const { execute: saveMovieToApi, error: saveMovieApiError } = usePatch<
    MovieResponse,
    UpdateMovieRequest
  >(updateMovieUrl);
  const { execute: deleteMovieFromApi } = useDelete<void>(deleteMovieUrl);

  const resolvePreviewMovie = (movie: MovieSummary): MovieSummary => {
    const movieId = getMovieSummaryId(movie);
    const fromAll = userAllMovies.find((item) => getMovieSummaryId(item) === movieId);
    const fromRecent = userRecentMovies.find((item) => getMovieSummaryId(item) === movieId);
    return mergeMovieSummaries(movie, fromRecent, fromAll) ?? movie;
  };

  const applySummaryUpdates = (movieId: number, updates: Partial<MovieSummary>) => {
    const patchList = (list: MovieSummary[]) =>
      list.map((movie) =>
        getMovieSummaryId(movie) === movieId ? { ...movie, ...updates } : movie
      );

    setUserAllMovies(patchList(userAllMovies));
    setUserRecentMovies(patchList(userRecentMovies));
    setPreviewMovie((current) =>
      current && getMovieSummaryId(current) === movieId ? { ...current, ...updates } : current
    );

    if (currentMovieId === movieId) {
      updateMovie({
        name: updates.name ?? currentMovie?.name,
        description: updates.description ?? currentMovie?.description,
        width: updates.width ?? currentMovie?.width,
        height: updates.height ?? currentMovie?.height,
      });
    }
  };

  const handlePreviewMovie = (movie: MovieSummary) => {
    const resolved = resolvePreviewMovie(movie);
    setShowNewMovieForm(false);
    setNewMovieName('');
    setNewMovieDescription('');
    setPreviewMovie(resolved);
    setIsEditing(false);
    setEditedMovieName(resolved.name ?? '');
    setEditedMovieDescription(resolved.description ?? '');
    setSaveError('');
  };

  const clearPreview = () => {
    setPreviewMovie(null);
    setIsEditing(false);
    setSaveError('');
  };

  useOnClickOutside(containerRef, (event) => {
    if (isDeleteConfirmOpen) {
      return;
    }

    // Radix menus/dialogs portal outside this container; ignore those clicks
    // so selecting a preview "..." action does not close the whole panel.
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(
        '[data-radix-popper-content-wrapper], [data-radix-menu-content], [role="dialog"]'
      )
    ) {
      return;
    }

    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (!isOpen) {
      clearPreview();
      setShowNewMovieForm(false);
      return;
    }

    if (!currentMovie || !currentMovieId || Number.isNaN(currentMovieId)) {
      return;
    }

    const fromLists =
      userAllMovies.find((item) => getMovieSummaryId(item) === currentMovieId) ??
      userRecentMovies.find((item) => getMovieSummaryId(item) === currentMovieId);

    handlePreviewMovie(
      fromLists ?? {
        id: currentMovieId,
        name: currentMovie.name,
        description: currentMovie.description,
        width: currentMovie.width,
        height: currentMovie.height,
        fps: currentMovie.fps,
        isSample: false,
      }
    );
    // Only seed preview when the selector opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!previewMovie || isEditing) return;

    const resolved = resolvePreviewMovie(previewMovie);
    if (isSamePreviewSummary(previewMovie, resolved)) return;

    setPreviewMovie(resolved);
    setEditedMovieName(resolved.name ?? '');
    setEditedMovieDescription(resolved.description ?? '');
  }, [previewMovie, isEditing, userAllMovies, userRecentMovies]);

  if (!isOpen) {
    return null;
  }

  const handleCreateNewMovie = async () => {
    if (!newMovieName.trim()) return;

    const newMovie = await createMovie({ name: newMovieName });

    if (newMovie) {
      const movieSummary: MovieSummary = {
        id: newMovie.id,
        name: newMovie.name,
        description: newMovieDescription.trim(),
        width: normalizeDimension(newMovie.width),
        height: normalizeDimension(newMovie.height),
        fps: newMovie.fps,
        sceneCount: newMovie.sceneCount ?? 0,
        sceneObjectCount: newMovie.sceneObjectCount ?? 0,
        layerCount: newMovie.layerCount ?? 0,
        isSample: false,
      };
      setUserAllMovies([...userAllMovies, movieSummary]);
      setSelectedMovieId(newMovie.id);
      onClose();
    }

    setShowNewMovieForm(false);
    setNewMovieName('');
    setNewMovieDescription('');
  };

  const handleStartEditing = () => {
    if (!previewMovie) return;
    setEditedMovieName(previewMovie.name ?? '');
    setEditedMovieDescription(previewMovie.description ?? '');
    setSaveError('');
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setSaveError('');
    setEditedMovieName(previewMovie?.name ?? '');
    setEditedMovieDescription(previewMovie?.description ?? '');
  };

  const handleSavePreviewMovie = async () => {
    if (!previewMovie || previewMovieId === null) {
      setSaveError(t('movieSelector.errors.idUnresolved'));
      return;
    }

    const trimmedName = editedMovieName.trim();
    if (!trimmedName) {
      setSaveError(t('movieSelector.errors.nameEmpty'));
      return;
    }

    setSaveError('');
    const safeWidth = normalizeDimension(previewMovie.width ?? 0);
    const safeHeight = normalizeDimension(previewMovie.height ?? 0);
    const safeFps = previewMovie.fps && previewMovie.fps > 0 ? previewMovie.fps : 30;
    const trimmedDescription = editedMovieDescription.trim();

    try {
      const response = await saveMovieToApi({
        name: trimmedName,
        description: trimmedDescription,
        width: safeWidth,
        height: safeHeight,
        fps: safeFps,
      });

      if (response) {
        applySummaryUpdates(previewMovieId, {
          name: trimmedName,
          description: trimmedDescription,
          width: safeWidth,
          height: safeHeight,
          fps: safeFps,
        });
        setIsEditing(false);
        return;
      }

      setSaveError(saveMovieApiError || t('movieSelector.errors.saveFailed'));
    } catch (error) {
      console.error('Failed to save movie:', error);
      setSaveError(
        saveMovieApiError ||
          (error instanceof Error ? error.message : t('movieSelector.errors.saveFailed'))
      );
    }
  };

  const getRecentMovieDate = (movieSummary: MovieSummary): Date | null => {
    const candidateDate =
      movieSummary.lastOpenedAt ?? movieSummary.updatedAt ?? movieSummary.createdAt;
    if (!candidateDate) return null;

    const parsed = new Date(candidateDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getDaysAgo = (targetDate: Date): number => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetStart = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );
    const diffMs = todayStart.getTime() - targetStart.getTime();

    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getRecentSectionLabel = (daysAgo: number): string => {
    if (daysAgo <= 0) return t('movieSelector.today');
    if (daysAgo === 1) return t('movieSelector.yesterday');
    return t('movieSelector.daysAgo', { count: daysAgo });
  };

  const groupedRecentMovies = userRecentMovies.reduce<Record<string, MovieSummary[]>>(
    (groups, movieSummary) => {
      const movieDate = getRecentMovieDate(movieSummary);
      const daysAgo = movieDate ? getDaysAgo(movieDate) : 999;
      const sectionLabel = movieDate
        ? getRecentSectionLabel(daysAgo)
        : t('movieSelector.older');

      if (!groups[sectionLabel]) {
        groups[sectionLabel] = [];
      }

      groups[sectionLabel].push(movieSummary);
      return groups;
    },
    {}
  );

  const sortedRecentSectionLabels = Object.keys(groupedRecentMovies).sort((a, b) => {
    const first = groupedRecentMovies[a][0];
    const second = groupedRecentMovies[b][0];
    const firstDate = first ? getRecentMovieDate(first) : null;
    const secondDate = second ? getRecentMovieDate(second) : null;
    const firstDaysAgo = firstDate ? getDaysAgo(firstDate) : Number.MAX_SAFE_INTEGER;
    const secondDaysAgo = secondDate ? getDaysAgo(secondDate) : Number.MAX_SAFE_INTEGER;

    return firstDaysAgo - secondDaysAgo;
  });

  const handleDeleteMovie = async () => {
    if (!previewMovie || previewMovieId === null) {
      setSaveError(t('movieSelector.errors.idUnresolved'));
      return;
    }

    await deleteMovieFromApi();
    deleteMovie(previewMovieId);

    if (isPreviewCurrentMovie) {
      unloadMovie();
      setWelcomeOpen(true);
      setIsDeleteConfirmOpen(false);
      onClose();
      return;
    }

    clearPreview();
    setIsDeleteConfirmOpen(false);
  };

  const handleUnloadMovie = () => {
    unloadMovie();
    setWelcomeOpen(true);
    setIsEditing(false);
    onClose();
  };

  const handleOpenPreviewedMovie = () => {
    if (!previewMovie || previewMovieId === null) return;
    setSelectedMovieId(previewMovieId);
    onClose();
  };

  const width = previewMovie?.width && previewMovie.width > 0 ? previewMovie.width : null;
  const height = previewMovie?.height && previewMovie.height > 0 ? previewMovie.height : null;
  const fps = previewMovie?.fps && previewMovie.fps > 0 ? previewMovie.fps : null;

  const renderMovieListItem = (movieSummary: MovieSummary) => {
    const movieId = getMovieSummaryId(movieSummary);
    const isActive = previewMovieId === movieId;

    return (
      <li
        key={movieSummary.id}
        className={`movie-list-item${isActive ? ' is-active' : ''}`}
        onClick={() => handlePreviewMovie(movieSummary)}
      >
        <FilmSlate className="movie-list-icon" aria-hidden="true" />
        <span className="movie-list-name">{movieSummary.name}</span>
      </li>
    );
  };

  return (
    <div className="movie-selection-area relative z-50" ref={containerRef}>
      <div className="movie-selection-modal absolute top-0 left-0 z-50">
        <div className="movie-selection-lists">
          <div className="movie-create-area">
            {!showNewMovieForm && (
              <Button
                variant="soft"
                size="2"
                className="movie-create-button"
                onClick={() => {
                  clearPreview();
                  setShowNewMovieForm(true);
                }}
              >
                <PlusIcon aria-hidden="true" />
                {t('movieSelector.newMovie')}
              </Button>
            )}

            {showNewMovieForm && (
              <div className="create-movie-form">
                <div className="create-movie-form-header">
                  <span className="create-movie-form-label">
                    {t('movieSelector.createNewMovie')}
                  </span>
                </div>
                <input
                  type="text"
                  className="input"
                  placeholder={t('movieSelector.movieNamePlaceholder')}
                  value={newMovieName}
                  onChange={(e) => setNewMovieName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateNewMovie();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setShowNewMovieForm(false);
                      setNewMovieName('');
                      setNewMovieDescription('');
                    }
                  }}
                  autoFocus
                />
                <textarea
                  className="input"
                  rows={3}
                  placeholder={t('movieSelector.movieDescriptionPlaceholder')}
                  value={newMovieDescription}
                  onChange={(e) => setNewMovieDescription(e.target.value)}
                />
                <div className="create-movie-form-actions">
                  <Button
                    variant="soft"
                    size="2"
                    disabled={!newMovieName.trim()}
                    onClick={() => void handleCreateNewMovie()}
                  >
                    <PlusIcon aria-hidden="true" />
                    {t('common.create')}
                  </Button>
                  <Button
                    variant="surface"
                    size="2"
                    color="gray"
                    onClick={() => {
                      setShowNewMovieForm(false);
                      setNewMovieName('');
                      setNewMovieDescription('');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Collapsible.Root defaultOpen>
            <Collapsible.Trigger className="collapsible-trigger">
              <span className="collapsible-arrow">
                <ChevronRightIcon />
              </span>
              <label className="text-md font-light text-theme-primary">
                {t('movieSelector.recent')}
              </label>
            </Collapsible.Trigger>
            <Collapsible.Content className="collapsible-content">
              <ul className="movie-list">
                {sortedRecentSectionLabels.map((sectionLabel) => (
                  <li key={sectionLabel} className="movie-list-group">
                    <div className="movie-list-group-title">{sectionLabel}</div>
                    <ul className="movie-list">
                      {groupedRecentMovies[sectionLabel].map(renderMovieListItem)}
                    </ul>
                  </li>
                ))}
              </ul>
            </Collapsible.Content>
          </Collapsible.Root>
          <Collapsible.Root defaultOpen>
            <Collapsible.Trigger className="collapsible-trigger">
              <span className="collapsible-arrow">
                <ChevronRightIcon />
              </span>
              <label className="text-md font-light text-theme-primary">
                {t('movieSelector.myProjects')}
              </label>
            </Collapsible.Trigger>
            <Collapsible.Content className="collapsible-content">
              <ul className="movie-list">
                {userAllMovies.map(renderMovieListItem)}
              </ul>
            </Collapsible.Content>
          </Collapsible.Root>
        </div>

        <div className="movie-selection-preview">
          {previewMovie ? (
            <>
              <div className="movie-selection-preview-header">
                <span className="movie-selection-preview-title">
                  {isPreviewCurrentMovie
                    ? t('movieSelector.currentMovie')
                    : t('welcome.previewTitle')}
                </span>
                <div className="movie-selection-preview-header-actions">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <Button
                        variant="ghost"
                        size="1"
                        className="movie-selection-more-button"
                        aria-label={t('movieSelector.moreActions')}
                      >
                        <DotsHorizontalIcon aria-hidden="true" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content variant="soft" size="2" align="end" sideOffset={4}>
                      <DropdownMenu.Item onSelect={handleStartEditing}>
                        <Pencil1Icon aria-hidden="true" />
                        {t('common.edit')}
                      </DropdownMenu.Item>
                      {isPreviewCurrentMovie && (
                        <DropdownMenu.Item onSelect={handleUnloadMovie}>
                          <ExitIcon aria-hidden="true" />
                          {t('movieSelector.closeMovie')}
                        </DropdownMenu.Item>
                      )}
                      <DropdownMenu.Item
                        color="red"
                        onSelect={() => setIsDeleteConfirmOpen(true)}
                      >
                        <TrashIcon aria-hidden="true" />
                        {t('movieSelector.deleteMovie')}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                  <Button
                    variant="ghost"
                    size="1"
                    className="movie-selection-preview-close"
                    onClick={onClose}
                    aria-label={t('common.close')}
                  >
                    <Cross1Icon aria-hidden="true" />
                  </Button>
                </div>
              </div>

              <div className="movie-selection-preview-body">
                <div className="movie-selection-preview-identity">
                  <FilmSlate className="movie-selection-icon" aria-hidden="true" />
                  <div className="movie-selection-preview-text">
                    {isEditing ? (
                      <div className="movie-selection-preview-edit-fields">
                        <input
                          type="text"
                          className="input"
                          value={editedMovieName}
                          onChange={(e) => setEditedMovieName(e.target.value)}
                          autoFocus
                        />
                        <textarea
                          value={editedMovieDescription}
                          placeholder={t('movieSelector.movieDescriptionPlaceholder')}
                          rows={3}
                          className="input"
                          onChange={(e) => setEditedMovieDescription(e.target.value)}
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="movie-selection-preview-name">{previewMovie.name}</h3>
                        <p className="movie-selection-preview-description">
                          {previewMovie.description?.trim()
                            ? previewMovie.description
                            : t('welcome.noDescription')}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {saveError && <div className="movie-selection-save-error">{saveError}</div>}

                <dl className="movie-selection-preview-meta">
                  {(width !== null || height !== null) && (
                    <div className="movie-selection-preview-meta-row">
                      <dt>{t('welcome.resolution')}</dt>
                      <dd>
                        {width ?? '—'} × {height ?? '—'}
                      </dd>
                    </div>
                  )}
                  {fps !== null && (
                    <div className="movie-selection-preview-meta-row">
                      <dt>{t('welcome.fps')}</dt>
                      <dd>{t('welcome.fpsValue', { fps })}</dd>
                    </div>
                  )}
                  <div className="movie-selection-preview-meta-row">
                    <dt>{t('welcome.scenes')}</dt>
                    <dd>{previewMovie.sceneCount ?? 0}</dd>
                  </div>
                  <div className="movie-selection-preview-meta-row">
                    <dt>{t('welcome.sceneObjects')}</dt>
                    <dd>{previewMovie.sceneObjectCount ?? 0}</dd>
                  </div>
                  <div className="movie-selection-preview-meta-row">
                    <dt>{t('welcome.layers')}</dt>
                    <dd>{previewMovie.layerCount ?? 0}</dd>
                  </div>
                </dl>
              </div>

              <div className="movie-selection-preview-actions">
                {isEditing ? (
                  <>
                    <Button
                      variant="soft"
                      size="3"
                      onClick={() => void handleSavePreviewMovie()}
                    >
                      {t('common.save')}
                    </Button>
                    <Button variant="surface" size="3" color="gray" onClick={handleCancelEditing}>
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  !isPreviewCurrentMovie && (
                    <Button variant="solid" size="3" onClick={handleOpenPreviewedMovie}>
                      {t('welcome.openProject')}
                    </Button>
                  )
                )}
              </div>
            </>
          ) : (
            <div className="movie-selection-placeholder">
              {t('movieSelector.selectToPreview')}
            </div>
          )}
        </div>

        <ConfirmModal
          open={isDeleteConfirmOpen}
          onOpenChange={setIsDeleteConfirmOpen}
          title={t('movieSelector.deleteConfirmTitle')}
          description={t('movieSelector.deleteConfirmDescription', {
            name: previewMovie?.name ?? t('movieSelector.thisMovie'),
          })}
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
          onConfirm={() => void handleDeleteMovie()}
        />
      </div>
    </div>
  );
}
